import { useTheme } from '@mui/material/styles';
import { Box, Container, Typography, Button, useMediaQuery } from "@mui/material"
import BookBtn from "components/ui/bookbtn";
import { path } from "config/path";

const HairSpa = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box display='block' sx={{ p:'2em 1em', textAlign:'center'}}>
      
      <Container maxWidth="md">
        {/* Header */}
        <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#1a4314' }}>
          🌿 Jubilee Premium Korean Head Spa
        </Typography>
        <Typography variant="h2" component="p" paragraph sx={{ color: '#4CAF50' }}>
          Redefining Scalp Wellness in London with HISTEMO
        </Typography>
        <Typography variant="h4" component="p" paragraph sx={{ fontStyle: 'italic', color: '#666' }}>
          Beyond Beauty — The Science of Scalp Care
        </Typography>

        {/* --- Video Section 1 --- */}
        <Box sx={{ my: 4 }}>
          <video 
            width="100%" 
            height="auto" 
            controls
            autoPlay 
            loop 
            muted 
            playsInline
          >
            <source src={`${path.basename}/videos/Instaupload.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>

        {/* Content Section 1 */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" paragraph>
            At Jubilee, we see scalp care not as an occasional indulgence, but as a foundation of lasting beauty and
            confidence. Our Premium Korean Head Spa is powered by <Typography component="span" sx={{ fontWeight: 'bold' }}>HISTEMO</Typography>, Korea’s leading scalp and hair science brand,
            combining advanced dermatological research with natural ingredients.
          </Typography>
          <Typography variant="h4" paragraph>
            Healthy skin begins with skincare. Healthy hair begins with scalp care.
          </Typography>
        </Box>

        {/* --- The HISTEMO Difference --- */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1a4314' }}>
            The HISTEMO Difference
          </Typography>
          <Typography variant="h4" paragraph>
            Founded in Korea and trusted by professional salons worldwide, HISTEMO products are formulated with
            plant-derived stem cell extracts, peptides, and botanical phytoceuticals. Each product is
            dermatologically tested, targeting key concerns such as:
          </Typography>
          <Box component="ul" sx={{ textAlign: 'left', listStylePosition: 'inside', pl: 0 }}>
            <Typography component="li" variant="h4">Hair thinning & early-stage hair loss</Typography>
            <Typography component="li" variant="h4">Sensitive or inflamed scalp</Typography>
            <Typography component="li" variant="h4">Dryness, dandruff, or excess oil</Typography>
            <Typography component="li" variant="h4">Environmental stress and ageing of the scalp</Typography>
          </Box>
          <Typography variant="h4" paragraph sx={{ mt: 2, fontStyle: 'italic', color: '#666' }}>
            ✨ With HISTEMO, every treatment is a fusion of science-backed solutions and K-beauty rituals, ensuring visible, long-lasting results.
          </Typography>
        </Box>
        
        {/* --- Video Section 2 --- */}
        <Box sx={{ my: 4 }}>
          <video 
            width="100%" 
            height="auto" 
            controls 
            autoPlay 
            loop 
            muted 
            playsInline
          >
            <source src={`${path.basename}/videos/Shortversion.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>

        {/* --- Your Experience at Jubilee --- */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1a4314' }}>
            Your Experience at Jubilee
          </Typography>
          <Typography variant="h3" paragraph>
            Our Premium Head Spa is designed as a restorative ritual:
          </Typography>
          <Box component="ol" sx={{ textAlign: 'left', listStylePosition: 'inside', pl: 0 }}>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Diagnosis & Consultation</Typography> – Professional scalp analysis to understand your unique condition.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Purifying Cleansing</Typography> – Gentle cleansing with soft water to remove impurities while protecting moisture.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>HISTEMO Therapy</Typography> – Advanced serums and masks enriched with stem cell and peptide complexes, tailored to your scalp needs.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Korean Massage Techniques</Typography> – Relieves stress, stimulates circulation, and enhances nutrient absorption.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Revitalisation & Balance</Typography> – Hydration and nourishment restore scalp comfort and leave your hair healthier, denser, and shinier.
            </Typography>
          </Box>
        </Box>

        {/* --- Why Choose Jubilee? --- */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1a4314' }}>
            Why Choose Jubilee?
          </Typography>
          <Box component="ul" sx={{ textAlign: 'left', listStylePosition: 'inside', pl: 0 }}>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Exclusive in London</Typography> – Authentic Korean Head Spa powered by HISTEMO.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Scientific + Holistic</Typography> – Dermatological care paired with relaxation.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Tailored Rituals</Typography> – Customised 1:1 treatments for every scalp type.
            </Typography>
            <Typography component="li" variant="h4">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Long-Term Results</Typography> – Strength, shine, and scalp health that last beyond the salon visit.
            </Typography>
          </Box>
        </Box>

        {/* --- Recommended Routine --- */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1a4314' }}>
            Recommended Routine
          </Typography>
          <Typography variant="h4" paragraph>
            As the scalp renews itself every 28 days, we recommend a monthly Head Spa session for overall maintenance, or more frequent visits for those with specific scalp concerns.
          </Typography>
        </Box>

        {/* --- Book Your Premium Korean Head Spa --- */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1a4314' }}>
            Book Your Premium Korean Head Spa
          </Typography>
          <Typography variant="h4" paragraph>
            Step into Jubilee and discover the future of scalp care.
            With the science of HISTEMO and the artistry of Korean beauty rituals, our Head Spa delivers more than relaxation — it restores balance, confidence, and vitality from the root.
          </Typography>
          <Typography variant="h3" component="p" sx={{ mt: 4, fontWeight: 'bold', fontStyle: 'italic', color: '#1a4314' }}>
            ✨ Book your appointment today and experience London’s most exclusive Korean Head Spa.
          </Typography>
          {/* <Button variant="contained" sx={{ mt: 3, bgcolor: '#1a4314', '&:hover': { bgcolor: '#2e7d32' } }}>
            BOOK ONLINE
          </Button> */}
          <Box sx={{ mt:3, textAlign:'center', px:'3.2em' }}>
            <BookBtn />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HairSpa;